/**
 * 游戏壳上那颗悬浮"返回大厅"键停在哪。**纯函数**，拖拽手势本身在 GameBox.tsx。
 *
 * ## 为什么要能拖
 *
 * 它压在一包**我们改不了的**游戏画面上。参考工程（casinoh5 的 gameBox.vue）
 * 是写死在左上角的 —— 那一格在 Fortune Rabbit 里正好是游戏标题，
 * 换一款游戏又是另一个按钮。写死在哪都会挡住某款游戏的某样东西，
 * 所以让玩家自己挪开，松手吸到最近的一侧（不停在画面中间挡转轮）。
 *
 * ## 为什么存的是"哪一侧 + 高度比例"，不是像素
 *
 * 转屏、收起地址栏、换一台手机，视口高度都会变。存像素的话，
 * 上次停在底部的键下次可能在屏幕外面 —— 那等于**返回入口没了**。
 * 比例永远落在可见区里。
 */
export type Side = 'left' | 'right';
export interface Dock { side: Side; y: number }

/** 默认停在左上、让开游戏标题那一条。 */
export const DEFAULT_DOCK: Dock = { side: 'left', y: 0.06 };

/** 手指挪超过这么多才算拖，不算点。太小的话手抖一下就点不中。 */
export const DRAG_SLOP = 6;

const KEY = 'lobby.gameDock';

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * 按钮（左上角）在 `w×h` 的层里的像素位置。
 * `size` 是按钮边长，`margin` 是离层边缘至少留多少。
 */
export function place(d: Dock, w: number, h: number, size: number, margin: number) {
  const range = Math.max(0, h - size - margin * 2);
  return {
    left: d.side === 'left' ? margin : Math.max(margin, w - size - margin),
    top: margin + clamp01(d.y) * range,
  };
}

/** 松手时按钮中心在 (cx, cy)：吸到近的那一侧，高度换算成比例。 */
export function snap(cx: number, cy: number, w: number, h: number,
                     size: number, margin: number): Dock {
  const range = h - size - margin * 2;
  return {
    side: cx < w / 2 ? 'left' : 'right',
    y: range > 0 ? clamp01((cy - size / 2 - margin) / range) : 0,
  };
}

/** 存下来的东西**可能是任何样子**（旧版本、手改、写了一半）。认不出就用默认值。 */
export function parseDock(raw: string | null | undefined): Dock {
  try {
    const o = JSON.parse(raw ?? '') as Partial<Dock>;
    if (o && (o.side === 'left' || o.side === 'right')
        && typeof o.y === 'number' && Number.isFinite(o.y)) {
      return { side: o.side, y: clamp01(o.y) };
    }
  } catch { /* 落到默认值 */ }
  return DEFAULT_DOCK;
}

export function loadDock(store: Pick<Storage, 'getItem'> | null): Dock {
  try { return parseDock(store?.getItem(KEY)); } catch { return DEFAULT_DOCK; }
}

export function saveDock(store: Pick<Storage, 'setItem'> | null, d: Dock): void {
  try { store?.setItem(KEY, JSON.stringify(d)); } catch { /* 隐私模式：这次仍然有效 */ }
}
